import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { Container, CapthaWrapper } from './SignUpScreen.styles';
// import { Typography } from '@mui/material';
// import { SignUpScreenStrings } from '../../../common/localization/en';
import { getValidationSchema } from '../../MagicLinkPage/MagicLinkForm/MagicLinkForm.helper';
import { useFormik } from 'formik';
// import { useClientType } from '../../../services/hooks/useClientType';
// import { TermsOfUse } from './TermsOfUse/TermsOfUse';
import { useAppDispatch, useTypedSelector } from '../../../store/store';
import { VideoToolPage } from '../../VideoToolPage/VideoToolPage';
import { useQuery } from '../../../services/hooks/useQuery';
import { loginUnverified, updateUserRequest } from '../../../store/slices/me';
import { ReCaptcha, ReCaptchaRefType } from '../../../common/components/FormComponents/ReCAPTCHA/ReCaptcha';
import { EventNames } from '../../../common/constants/constants';
import { useTrackEvent } from '../../../common/hooks/useTrackEvent';
import { getAppLinkSource } from '../../../services/utilities';
import { changeStep } from '../../../store/slices/upload';
import { UploadProcessStep } from '../../../store/storeModels';
import { setHideCrispChat, setIdentityOverlayOpen } from '../../../store/slices/ui';
// import { CampaignTypes } from '../../../api/models/rewards';
// import { SocialUploadPrompt } from '../../../common/components/SocialUploadPrompt/SocialUploadPrompt';
// import { PortalWrapper } from '../../../common/components/PortalWrapper/PortalWrapper';
import { HomeScreen } from '../../CAV6/HomeScreen/HomeScreen';
import { IdentityBlock } from '../../CAV6/UploadFlowCarousel/blocks/IdentityBlock';
import { UploadChoiceSheet } from '../../CAV6/UploadFlowCarousel/UploadChoiceSheet';
import { UploadStatus, UploadTicket, useUploadDone } from '../../CAV6/UploadFlowCarousel/UploadTicket';
import { TICKET } from '../../CAV6/HomeScreen/HomeScreen.styles';
import { usePendingUpload } from '../../../services/upload/usePendingUpload';
import { fingerprintsMatch } from '../../../services/upload/fingerprint';
import { getUploadDimensions } from '../../../services/upload/uploadDimensions';
import { maxSizeUploadedUserFile } from '../../../common/constants/constants';
import {
  getAttempt,
  oncePerPageLoad,
  registerAbandonStage,
  setAttemptMeta,
  startAttempt,
} from '../../../services/analytics/telemetry';

export const BottomPercent = '35%';

interface Props {
  isRecordVideoScreen: boolean;
  setIsRecordVideoScreen: (isRecordScreen: boolean) => void;
  isTermsAccepted: boolean | null;
  onTermsChange: (isChecked: boolean | null) => void;
  isDataLoading: boolean;
}

const CAPTCHA_FAIL_SUBDOMAINS = ['klsvw', 'bzdur'];

export const SignUpScreen: FC<Props> = ({
  isRecordVideoScreen,
  setIsRecordVideoScreen,
  isTermsAccepted,
  onTermsChange,
  isDataLoading,
}) => {
  // const { isTrubluClient } = useClientType();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const brandColor = theme.palette.primary.main;
  const uploadDone = useUploadDone();
  const { trackEvent } = useTrackEvent();
  const {
    account: { id: accountId, subdomain },
    accountAttributes: { properties: accountProperties },
  } = useTypedSelector((state) => state.account);
  // Per-account switch. Missing means enabled; only an explicit false turns it
  // off. If the server still answers 418 (captcha required), the setting is
  // treated as stale for this page load and the checkbox fallback takes over.
  // Two names for the same switch: `webapp.captcha-enabled` (requested) and
  // `webapp.captcha-required` (what the backend stores on accounts today).
  // Either one explicitly false disables it.
  const isOff = (v: unknown) => v === false || v === 'false';
  const captchaDisabledByAccount =
    isOff(accountProperties?.['webapp.captcha-enabled']) || isOff(accountProperties?.['webapp.captcha-required']);
  const [captchaForcedByServer, setCaptchaForcedByServer] = useState(false);
  const captchaEnabled = !captchaDisabledByAccount || captchaForcedByServer;
  const { hideCrispChat } = useTypedSelector((state) => state.ui);
  const [isSignUpFormHidden, setIsSignUpFormHidden] = useState(false);
  const [isVerificationScreen, setIsVerificationScreen] = useState(false);
  const [captcharesponse, setCaptcharesponse] = useState('');
  const [externalFile, setExternalFile] = useState<File | null>(null);
  const [showIdentityBlock, setShowIdentityBlock] = useState(false);
  // The upload flow underneath holds its business step until this closes.
  useEffect(() => {
    dispatch(setIdentityOverlayOpen(showIdentityBlock));
  }, [dispatch, showIdentityBlock]);
  useEffect(
    () => () => {
      dispatch(setIdentityOverlayOpen(false));
    },
    [dispatch],
  );
  const [resumeFrom, setResumeFrom] = useState<{ id: string; uploadUrl: string } | null>(null);
  const [showResumeSheet, setShowResumeSheet] = useState(false);
  const resumeInputRef = useRef<HTMLInputElement | null>(null);
  const { pendingUpload, ready: pendingReady, clearPendingUpload } = usePendingUpload();
  // const { currentCampaign } = useTypedSelector((state) => state.rewards);

  // const isCustomRewardCampaign = currentCampaign?.campaignType === CampaignTypes.CUSTOM_REWARD;
  // const isMonthlyGiveawayCampaign = currentCampaign?.campaignType === CampaignTypes.MONTHLY_GIVEAWAY;
  // const isNoRewardCampaign = currentCampaign?.campaignType === CampaignTypes.NO_REWARD;

  //   const questions = [IncentiveBlockStrings.Question1, IncentiveBlockStrings.Question2];
  // const { isUVMClient } = useClientType();
  // const { signUpFlowActiveTabIndex } = useTypedSelector((state) => state.ui);
  const {
    id: userId,
    firstName: meFirstName,
    lastName: meLastName,
    registerErrorType,
  } = useTypedSelector((state) => state.me);

  // `loginUnverified` mints a userId at file-pick time so the upload can run
  // in parallel with identity collection. The user's actual name/phone only
  // lands when the IdentityBlock overlay's onConfirm fires — until then the
  // me slice has empty/placeholder fields. If the user closed the tab after
  // file-pick but before submitting IdentityBlock, `userId` is persisted but
  // identity isn't. On cross-session resume, gating on `!userId` would skip
  // the IdentityBlock and drop the user straight into the questions block.
  // Check first/last name as the real "identity collected" signal.
  const identityCollected = !!userId && !!meFirstName && !!meLastName;

  const query = useQuery();
  const [isEnterpriseCaptcha, setIsEnterpriseCaptcha] = useState(true);
  const [captcha, setCaptcha] = useState<string | null>('');

  const { firstName, lastName, phoneNumber } = {
    firstName: query.get('svUn')?.split(' ')[0] || '',
    lastName: query.get('svUn')?.split(' ')[1],
    phoneNumber: query.get('phone') || '',
  };

  // Terms state is now managed by parent (SignUpFlowPage)
  const ecaptchaRef = useRef<ReCaptchaRefType>(null);

  const initialValues = {
    firstName: firstName || meFirstName,
    lastName: lastName || meLastName,
  };

  // type InitialValues = typeof initialValues;

  const { values } = useFormik({
    initialValues,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: getValidationSchema({
      firstName: true,
      lastName: true,
      email: false,
      phoneNumber: false,
    }),
    onSubmit: () => {},
  });

  useEffect(() => {
    // The screen mounts twice on load; the legacy page view counts the load.
    if (oncePerPageLoad('create_video_page_view')) trackEvent({ action: EventNames.create_video_page_view });
    if (hideCrispChat) {
      dispatch(setHideCrispChat(false));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsSignUpFormHidden(isRecordVideoScreen || isVerificationScreen);
  }, [isRecordVideoScreen, isVerificationScreen]);

  // Show the resume sheet once we've read storage AND the persisted record
  // is scoped to this account. Mismatched account → silently drop.
  useEffect(() => {
    if (!pendingReady || !pendingUpload) return;
    if (pendingUpload.accountId !== accountId) {
      // Different account — not relevant here; the other account's session
      // will surface when the user visits that slug.
      return;
    }
    setShowResumeSheet(true);
    trackEvent({
      action: EventNames.upload_resume_offered,
      accountId,
      fileSize: pendingUpload.totalBytes,
      ...getUploadDimensions(),
    });
    trackEvent({
      action: EventNames.resume_offer_view,
      age_hours: pendingUpload.updatedAt ? Math.round((Date.now() - pendingUpload.updatedAt) / 36e4) / 10 : null,
      pct_done: pendingUpload.totalBytes
        ? Math.round((100 * pendingUpload.uploadedBytes) / pendingUpload.totalBytes)
        : null,
      file_size: pendingUpload.totalBytes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReady, pendingUpload, accountId]);

  const handleResumeClick = () => {
    trackEvent({ action: EventNames.resume_offer_action, resume_action: 'repick' });
    resumeInputRef.current?.click();
  };

  const handleResumeFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (resumeInputRef.current) resumeInputRef.current.value = '';
    if (!file || !pendingUpload) return;

    if (file.size >= maxSizeUploadedUserFile) {
      trackEvent({ action: EventNames.resume_repick_result, outcome: 'too_large' });
      // File too large per app limit — fall through to normal flow which
      // surfaces the size error.
      clearPendingUpload();
      setShowResumeSheet(false);
      setExternalFile(file);
      if (!identityCollected) setShowIdentityBlock(true);
      return;
    }

    if (!fingerprintsMatch(pendingUpload.file, file)) {
      trackEvent({ action: EventNames.upload_resume_repick_mismatch, accountId });
      // User picked a different video. Confirm before discarding the
      // in-progress session to avoid accidental loss.
      const confirmed = window.confirm(
        'This looks like a different video than the one you started. Start a new upload with this file instead?',
      );
      trackEvent({
        action: EventNames.resume_repick_result,
        outcome: confirmed ? 'mismatch_confirmed_new' : 'mismatch',
      });
      if (!confirmed) return;
      clearPendingUpload();
      setShowResumeSheet(false);
      startAttempt('picker', file.size);
      setExternalFile(file);
      if (!identityCollected) setShowIdentityBlock(true);
      return;
    }

    trackEvent({
      action: EventNames.upload_resume_repick_match,
      accountId,
      fileSize: file.size,
      ...getUploadDimensions(),
    });
    trackEvent({
      action: EventNames.resume_repick_result,
      outcome: 'match',
      // Last locally acknowledged part. The server's 308 probe (reported on
      // preflight_complete.resumed_from_offset) is the authoritative offset.
      local_offset: pendingUpload.uploadedBytes,
    });
    startAttempt('resume_cross_session', file.size, { resumesAttemptId: pendingUpload.attemptId ?? null });
    // Match — resume the persisted session. setResumeFrom must precede
    // setExternalFile so VideoToolPage's effect sees the resume context.
    setResumeFrom({ id: pendingUpload.id, uploadUrl: pendingUpload.uploadUrl });
    setExternalFile(file);
    setShowResumeSheet(false);
    if (!userId) setShowIdentityBlock(true);
  };

  const handleResumeSheetClose = () => {
    trackEvent({ action: EventNames.upload_resume_dismissed, accountId });
    trackEvent({ action: EventNames.resume_offer_action, resume_action: 'dismiss' });
    setShowResumeSheet(false);
    // User dismissed the offer — clear the record so we don't re-prompt.
    clearPendingUpload();
  };

  const dataLoadingRef = useRef(isDataLoading);
  const campaignWaitersRef = useRef<Array<() => void>>([]);
  useEffect(() => {
    dataLoadingRef.current = isDataLoading;
    if (!isDataLoading) {
      campaignWaitersRef.current.splice(0).forEach((resolve) => resolve());
    }
  }, [isDataLoading]);
  const CAMPAIGN_WAIT_TIMEOUT_MS = 10_000;
  /** Resolves once the campaign has loaded, or after a bounded wait so a
   *  dead campaign request can never block the upload. */
  const waitForCampaign = useCallback((): Promise<'ready' | 'timeout'> => {
    if (!dataLoadingRef.current) return Promise.resolve('ready');
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve('timeout'), CAMPAIGN_WAIT_TIMEOUT_MS);
      campaignWaitersRef.current.push(() => {
        clearTimeout(timer);
        resolve('ready');
      });
    });
  }, []);

  const submitAuthHandler = useCallback(async (): Promise<{ success: boolean }> => {
    if (userId) {
      setAttemptMeta({
        userState: 'existing',
        captchaMode: !captchaEnabled ? 'disabled' : isEnterpriseCaptcha ? 'enterprise' : 'checkbox_fallback',
      });
      trackEvent({ action: EventNames.preflight_step, step: 'captcha_execute', outcome: 'skipped', ms: 0 });
      trackEvent({ action: EventNames.preflight_step, step: 'login_unverified', outcome: 'skipped', ms: 0 });
      return { success: true };
    }
    setAttemptMeta({
      userState: 'new_unverified',
      captchaMode: !captchaEnabled ? 'disabled' : isEnterpriseCaptcha ? 'enterprise' : 'checkbox_fallback',
    });

    const captchaStartedAt = performance.now();
    if (!captchaEnabled) {
      trackEvent({
        action: EventNames.preflight_step,
        step: 'captcha_execute',
        outcome: 'skipped',
        ms: 0,
        captcha_mode: 'disabled',
      });
    }
    let enterpriseCaptchaResponse: string | null | undefined = null;
    let captchaOutcome: 'ok' | 'fail' | 'skipped' = 'skipped';
    if (captchaEnabled && isEnterpriseCaptcha && navigator.onLine) {
      try {
        enterpriseCaptchaResponse = await ecaptchaRef.current?.executeAsync();
        captchaOutcome = enterpriseCaptchaResponse ? 'ok' : 'fail';
      } catch {
        enterpriseCaptchaResponse = null;
        captchaOutcome = 'fail';
      }
    }
    if (captchaEnabled) {
      trackEvent({
        action: EventNames.preflight_step,
        step: 'captcha_execute',
        outcome: captchaOutcome,
        ms: Math.round(performance.now() - captchaStartedAt),
        captcha_mode: isEnterpriseCaptcha ? 'enterprise' : 'checkbox_fallback',
      });
    }
    setCaptcharesponse(enterpriseCaptchaResponse || captcha || '');

    if (
      captchaEnabled &&
      ((!enterpriseCaptchaResponse && !isEnterpriseCaptcha && !captcha) ||
        (isEnterpriseCaptcha && CAPTCHA_FAIL_SUBDOMAINS.includes(subdomain)))
    ) {
      const reason =
        isEnterpriseCaptcha && CAPTCHA_FAIL_SUBDOMAINS.includes(subdomain) ? 'subdomain_list' : 'no_response';
      setIsEnterpriseCaptcha(false);
      trackEvent({ action: EventNames.captcha_fallback_shown, reason });
      trackEvent({
        action: EventNames.preflight_failed,
        failed_step: 'captcha_execute',
        error_code: reason,
        total_ms: Math.round(performance.now() - captchaStartedAt),
        will_retry: true,
      });
      return { success: false };
    }

    // Campaign id must be known before the user is created against it.
    const campaignWaitStartedAt = performance.now();
    const campaignWait = await waitForCampaign();
    trackEvent({
      action: EventNames.preflight_step,
      step: 'campaign_wait',
      outcome: campaignWait === 'ready' ? 'ok' : 'timeout',
      ms: Math.round(performance.now() - campaignWaitStartedAt),
    });

    const loginStartedAt = performance.now();

    const campaignId = localStorage.getItem('campaignId');
    const source = getAppLinkSource({ magicLink: false });

    const data = await dispatch(
      loginUnverified({
        email: null,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: phoneNumber || null,
        accountId,
        isEnterpriseCaptcha,
        captcha: enterpriseCaptchaResponse || captcha || '',
        skipCaptcha: !captchaEnabled,
        source,
        attributes: {
          associationId: localStorage.getItem('associationId') || null,
        },
        ...(campaignId && { campaignId }),
      }),
    );

    if (data.payload === 418) {
      // Captcha was skipped because the account disables it, but the server
      // still requires one: render the captcha from now on for this page.
      if (!captchaEnabled) setCaptchaForcedByServer(true);
      setIsEnterpriseCaptcha(false);
      trackEvent({
        action: EventNames.preflight_step,
        step: 'login_unverified',
        outcome: 'fail',
        ms: Math.round(performance.now() - loginStartedAt),
        http_status: 418,
      });
      trackEvent({ action: EventNames.captcha_fallback_shown, reason: captchaEnabled ? '418' : 'server_required' });
      trackEvent({
        action: EventNames.preflight_failed,
        failed_step: 'login_unverified',
        error_code: '418',
        total_ms: Math.round(performance.now() - captchaStartedAt),
        will_retry: true,
      });
      return { success: false };
    }

    const loginOk = typeof data.payload !== 'number' && !!data.payload;
    trackEvent({
      action: EventNames.preflight_step,
      step: 'login_unverified',
      outcome: loginOk ? 'ok' : 'fail',
      ms: Math.round(performance.now() - loginStartedAt),
      http_status: typeof data.payload === 'number' ? data.payload : null,
    });

    return { success: true };
  }, [
    dispatch,
    userId,
    isEnterpriseCaptcha,
    captchaEnabled,
    captcha,
    values.firstName,
    values.lastName,
    phoneNumber,
    accountId,
    registerErrorType,
    waitForCampaign,
  ]);

  // const isError = (fieldName: keyof InitialValues) => {
  //   return !!(touched[fieldName] && errors[fieldName]);
  // };

  // const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
  //   setFieldTouched(event.target.name);
  //   handleChange(event);
  // };

  // const showFormError = Object.keys(initialValues).some((key) => {
  //   return isError(key as keyof InitialValues);
  // });

  const fallbackShownAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isEnterpriseCaptcha) fallbackShownAtRef.current = performance.now();
  }, [isEnterpriseCaptcha]);

  // Preflight abandonment: an attempt exists but the engine hasn't started.
  useEffect(
    () =>
      registerAbandonStage(() => {
        const attempt = getAttempt();
        if (!attempt || attempt.completed) return null;
        return { stage: 'preflight' };
      }),
    [],
  );

  const onCaptchaChange = (value: string | null) => {
    setCaptcha(value);

    if (!value) {
      setIsEnterpriseCaptcha(false);
    }

    if (value && !isEnterpriseCaptcha) {
      trackEvent({
        action: EventNames.captcha_checkbox_solved,
        ms_since_shown: fallbackShownAtRef.current ? Math.round(performance.now() - fallbackShownAtRef.current) : null,
        retriggered_upload: !!(externalFile && !userId),
      });
    }

    // If manual captcha completed and a file is waiting, retry the upload
    if (value && externalFile && !userId) {
      // Reset externalFile to retrigger the VideoToolPage effect
      const file = externalFile;
      setExternalFile(null);
      setTimeout(() => setExternalFile(file), 0);
    }
  };

  // const isButtonsDisabled = (!isValid && !userId) || !isTermsAccepted || !captcha;
  // Buttons are live from first paint. The campaign id (written to
  // localStorage by HomePage when the campaign request returns) is only
  // needed by loginUnverified, so submitAuthHandler waits for it there —
  // behind the file picker, where the wait is almost always already over.
  const isButtonEnabled =
    (isTermsAccepted && userId) || (isTermsAccepted && (!captchaEnabled || isEnterpriseCaptcha || captcha));

  // const hideForUWMCustom = isUVMClient && isCustomRewardCampaign;
  // const hideCampaignInfo = true;

  return (
    <Container>
      {!isSignUpFormHidden && !showIdentityBlock && (
        <HomeScreen
          onFileSelect={(file) => {
            // Start upload immediately — loginUnverified creates an unverified user
            // so the upload can begin right away. IdentityBlock collects real info
            // concurrently while the upload is in progress.
            setExternalFile(file);
            if (!identityCollected) {
              setShowIdentityBlock(true);
            }
          }}
          onRecordClick={() => setIsRecordVideoScreen(true)}
          isButtonsDisabled={!isButtonEnabled}
          disabledReason={isButtonEnabled ? null : !isTermsAccepted ? 'terms' : 'captcha'}
          isTermsAccepted={isTermsAccepted}
          onTermsChange={onTermsChange}
        />
      )}

      {/* IdentityBlock overlay — shown on top of the upload flow to collect
          real user info while upload runs in the background. It is a full
          upload ticket of its own (status, the form, live chat), opaque over
          the carousel underneath, which mounts once auth succeeds. */}
      {showIdentityBlock && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            overflowY: 'auto',
            background: TICKET.pageBg,
          }}
        >
          <UploadTicket status={<UploadStatus complete={uploadDone} />}>
            <IdentityBlock
              initialFirstName={values.firstName}
              initialLastName={values.lastName}
              onConfirm={async ({ firstName: fn, lastName: ln, phone }) => {
                values.firstName = fn;
                values.lastName = ln;
                setShowIdentityBlock(false);

                // Update the unverified user with real info.
                // If it fails (e.g. 409 phone conflict), just let it fail silently.
                if (userId) {
                  dispatch(
                    updateUserRequest({
                      userId,
                      firstName: fn,
                      lastName: ln,
                      phoneNumber: phone || null,
                      accountId,
                    }),
                  ).then((result: { meta?: { requestStatus?: string }; payload?: unknown }) => {
                    const rejected = result?.meta?.requestStatus === 'rejected';
                    // The thunk rejects with { status, data } (see updateUserRequest).
                    const payload = result?.payload as { status?: number } | number | undefined;
                    const status =
                      typeof payload === 'number'
                        ? payload
                        : typeof payload?.status === 'number'
                        ? payload.status
                        : null;
                    trackEvent({
                      action: EventNames.identity_update_result,
                      outcome: !rejected ? 'ok' : status === 409 ? 'conflict_409' : 'error',
                      http_status: status,
                    });
                  });
                }
              }}
            />
          </UploadTicket>
        </Box>
      )}

      <Box
        width="100%"
        height={isSignUpFormHidden ? '100%' : '0'}
        sx={{ overflow: isSignUpFormHidden ? 'visible' : 'hidden' }}
      >
        {/* {!isRecordVideoScreen &&
          !isSignUpFormHidden &&
          !isNoRewardCampaign &&
          !hideForUWMCustom &&
          !hideCampaignInfo && (
            <Box sx={{ marginBottom: '80px' }}>
              {isMonthlyGiveawayCampaign ? (
                <Typography className={classes.getStartedText}>{SignUpScreenStrings.SweepStakeText1MG}</Typography>
              ) : (
                <Typography className={classes.getStartedText}>{SignUpScreenStrings.SweepStakeText1}</Typography>
              )}
              <Typography className={classes.sweepStakeTitle}>{SignUpScreenStrings.SweepStakeText2}</Typography>
              <Typography className={classes.sweepStakeDescription}>{SignUpScreenStrings.SweepStakeText3}</Typography>
            </Box>
          )} */}
        {/* {!userId && !isSignUpFormHidden && (
          <>
            <NameText>{SignUpScreenStrings.Name}</NameText>
            <Input
              value={values.firstName}
              onChange={handleFieldChange}
              disableUnderline={true}
              error={isError('firstName')}
              name="firstName"
              placeholder={SignUpScreenStrings.First}
              onBlur={handleBlur}
            />

            <Input
              value={values.lastName}
              onChange={handleFieldChange}
              disableUnderline={true}
              error={isError('lastName')}
              name="lastName"
              placeholder={SignUpScreenStrings.Last}
              onBlur={handleBlur}
            />
            {showFormError && (
              <ErrorText>{errors.firstName || errors.lastName}</ErrorText>
            )}
          </>
        )} */}

        <VideoToolPage
          isButtonsDisabled={!isButtonEnabled}
          isRecordVideoScreen={isRecordVideoScreen}
          setIsRecordVideoScreen={setIsRecordVideoScreen}
          captchaResponse={captcharesponse}
          externalFile={externalFile}
          resumeFrom={resumeFrom}
          hideCreateVideo={true}
          onUploadVideoStart={async () => {
            const { success } = await submitAuthHandler();
            if (!success) return false;

            setIsVerificationScreen(true);
            setIsRecordVideoScreen(false);
            dispatch(changeStep(UploadProcessStep.VideoUploading));
            // dispatch(setSignUpFlowActiveTabIndex(signUpFlowActiveTabIndex + 1));
            return true;
          }}
        />
        {/* Terms live on the ticket footer (HomeScreen) */}
      </Box>
      {!userId && captchaEnabled && (
        <CapthaWrapper
          display={isSignUpFormHidden ? 'none' : 'block'}
          style={{ width: isEnterpriseCaptcha ? '70px' : '100%', zIndex: 2000 }}
        >
          <ReCaptcha
            onChange={onCaptchaChange}
            key="captcha"
            ref={ecaptchaRef}
            onError={() => {
              setIsEnterpriseCaptcha(false);
              trackEvent({ action: EventNames.captcha_fallback_shown, reason: 'execute_error' });
            }}
            onExpired={() => {
              setIsEnterpriseCaptcha(false);
              trackEvent({ action: EventNames.captcha_fallback_shown, reason: 'expired' });
            }}
            isEnterprise={isEnterpriseCaptcha}
            onClick={() => {
              trackEvent({ action: EventNames.Captcha_Checkbox_Click });
            }}
          />
        </CapthaWrapper>
      )}

      {/* Cross-session resume offer. Rendered on top of HomeScreen when a
          non-expired, account-matched pendingUpload record is found at boot. */}
      <UploadChoiceSheet
        open={showResumeSheet}
        mode="resume"
        brandColor={brandColor}
        titleOverride="Finish your upload"
        bodyOverride={
          pendingUpload?.file?.name
            ? `Pick "${pendingUpload.file.name}" again to continue your upload from where you left off.`
            : undefined
        }
        onResume={handleResumeClick}
        onClose={handleResumeSheetClose}
      />
      <input
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        ref={resumeInputRef}
        className="visually-hidden"
        onChange={handleResumeFileChange}
      />
    </Container>
  );
};
